import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    Database, ChartBar, Warning, MapPin, Copy, Broadcast, Trophy,
    ArrowClockwise, CheckCircle, GitMerge, EyeSlash, ArrowSquareOut, CloudArrowDown,
} from "@phosphor-icons/react";

const BAND = {
    ottima: { label: "Ottima", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    buona: { label: "Buona", cls: "bg-sky-50 text-sky-700 border-sky-300" },
    incompleta: { label: "Incompleta", cls: "bg-amber-50 text-amber-700 border-amber-300" },
    da_verificare: { label: "Da verificare", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const TABS = [
    { key: "dashboard", label: "Dashboard", icon: ChartBar },
    { key: "score", label: "Quality Score", icon: Database },
    { key: "issues", label: "Issues", icon: Warning },
    { key: "geo", label: "Geo Validation", icon: MapPin },
    { key: "duplicates", label: "Duplicates", icon: Copy },
    { key: "acquisition", label: "Acquisition Monitor", icon: CloudArrowDown },
    { key: "failures", label: "Download Failures", icon: Warning },
    { key: "sources", label: "Sources Monitor", icon: Broadcast },
    { key: "top", label: "Top 100", icon: Trophy },
];

const ACQ_CLS = {
    verde: "bg-emerald-50 text-emerald-700 border-emerald-300",
    giallo: "bg-amber-50 text-amber-700 border-amber-300",
    rosso: "bg-rose-50 text-rose-700 border-rose-300",
    "n/a": "bg-gray-50 text-gray-500 border-gray-200",
};

function BandBadge({ band, testid }) {
    const m = BAND[band] || BAND.da_verificare;
    return <span data-testid={testid} className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function ScoreBar({ score }) {
    const color = score >= 90 ? "bg-emerald-500" : score >= 75 ? "bg-sky-500" : score >= 50 ? "bg-amber-500" : "bg-rose-500";
    return (
        <div className="h-2 w-24 bg-gray-100 overflow-hidden inline-block align-middle">
            <div className={`h-full ${color}`} style={{ width: `${score}%` }} />
        </div>
    );
}

const eur = (v) => (typeof v === "number" ? "€" + v.toLocaleString("it-IT") : "—");
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

// Maps technical download-failure reasons to friendly Italian labels.
// `compliance` reasons (robots.txt blocks) are NOT bugs → shown in amber, not red.
const REASON_INFO = {
    robots_disallow: { label: "Bloccato dal portale (compliance)", kind: "compliance" },
    connect_timeout: { label: "Timeout connessione", kind: "tech" },
    dns_error: { label: "Errore DNS (host non risolto)", kind: "tech" },
    network_error: { label: "Errore di rete", kind: "tech" },
    http_4xx: { label: "Errore portale (4xx)", kind: "tech" },
    http_5xx: { label: "Errore server portale (5xx)", kind: "tech" },
    parse_empty: { label: "Documento vuoto / non leggibile", kind: "tech" },
    status_0: { label: "Nessuna risposta dal portale", kind: "tech" },
    not_pdf: { label: "Il file non è un PDF valido", kind: "tech" },
};
function reasonInfo(reason) {
    if (!reason) return { label: "—", kind: "none" };
    if (REASON_INFO[reason]) return REASON_INFO[reason];
    if (reason.startsWith("attach_")) return { label: "Errore salvataggio documento", kind: "tech" };
    if (reason.startsWith("http_")) return { label: `Errore portale (${reason.replace("http_", "")})`, kind: "tech" };
    return { label: reason, kind: "tech" };
}
function ReasonBadge({ reason, testid }) {
    const info = reasonInfo(reason);
    if (info.kind === "none") return <span data-testid={testid} className="text-gray-400">—</span>;
    const cls = info.kind === "compliance"
        ? "bg-amber-50 text-amber-700 border-amber-300"
        : "bg-rose-50 text-rose-700 border-rose-300";
    return (
        <span data-testid={testid} title={reason}
            className={`inline-block text-[10px] font-semibold px-2 py-0.5 border ${cls}`}>
            {info.label}
        </span>
    );
}

// ---------------------------------------------------------------- Dashboard
function DashboardPane() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/quality/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
    if (!d) return <p className="text-sm text-gray-400" data-testid="dq-dashboard-loading">Caricamento…</p>;
    const cards = [
        { id: "total", label: "Aste totali", value: d.total },
        { id: "analyzed", label: "Analizzate", value: d.analyzed },
        { id: "archived", label: "Archiviate", value: d.archived },
        { id: "duplicates", label: "Duplicati (record)", value: d.duplicate_records },
        { id: "errors", label: "Con errori (DQ<50)", value: d.errors },
        { id: "missing-docs", label: "Documenti mancanti", value: d.missing_documents },
        { id: "geo-low", label: "Geo incoerenti", value: d.geo_low },
        { id: "dq-avg", label: "Data Quality medio", value: d.dq_avg, band: d.dq_band },
    ];
    return (
        <div className="space-y-6" data-testid="dq-dashboard-pane">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cards.map((c) => (
                    <div key={c.id} data-testid={`dq-card-${c.id}`} className="border border-gray-200 bg-white p-4">
                        <div className="text-2xl font-bold tabular-nums">{c.value}</div>
                        <div className="text-xs text-gray-500 mt-1">{c.label}</div>
                        {c.band && <div className="mt-2"><BandBadge band={c.band} /></div>}
                    </div>
                ))}
            </div>
            <div className="grid md:grid-cols-2 gap-5">
                <div data-testid="dq-by-region">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Aste per regione</h3>
                    <ul className="border border-gray-200 bg-white divide-y divide-gray-100">
                        {d.by_region.map((r) => (
                            <li key={r.region} className="flex justify-between px-3 py-1.5 text-sm">
                                <span>{r.region}</span><span className="font-semibold tabular-nums">{r.count}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div data-testid="dq-by-source-counts">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Aste per fonte</h3>
                    <ul className="border border-gray-200 bg-white divide-y divide-gray-100">
                        {d.by_source.map((r) => (
                            <li key={r.source} className="flex justify-between px-3 py-1.5 text-sm">
                                <span>{r.source}</span><span className="font-semibold tabular-nums">{r.count}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------- Quality Score
function ScorePane() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/quality/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
    if (!d) return <p className="text-sm text-gray-400">Caricamento…</p>;
    const Table = ({ title, rows, keyField, testid }) => (
        <div data-testid={testid}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{title}</h3>
            <div className="border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="px-3 py-2">{keyField === "region" ? "Regione" : "Fonte"}</th>
                        <th className="px-3 py-2">Score</th><th className="px-3 py-2">Classe</th><th className="px-3 py-2">Aste</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r[keyField]} className="border-b border-gray-50">
                                <td className="px-3 py-2 font-medium">{r[keyField]}</td>
                                <td className="px-3 py-2"><ScoreBar score={r.score} /> <span className="tabular-nums font-semibold ml-1">{r.score}</span></td>
                                <td className="px-3 py-2"><BandBadge band={r.band} /></td>
                                <td className="px-3 py-2 tabular-nums">{r.count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
    return (
        <div className="space-y-6" data-testid="dq-score-pane">
            <div className="border border-gray-200 bg-white p-5 flex items-center gap-4" data-testid="dq-avg-block">
                <div className="text-4xl font-bold tabular-nums">{d.dq_avg}</div>
                <div>
                    <div className="text-sm text-gray-500">Data Quality Score medio del database</div>
                    <BandBadge band={d.dq_band} />
                </div>
            </div>
            <Table title="Score per regione" rows={d.dq_by_region} keyField="region" testid="dq-score-region" />
            <Table title="Score per fonte" rows={d.dq_by_source} keyField="source" testid="dq-score-source" />
        </div>
    );
}

// -------------------------------------------------------------------- Issues
const ISSUE_TYPES = ["comune mancante", "provincia mancante", "indirizzo incompleto", "prezzo base mancante", "superficie mancante", "documento perizia mancante", "perizia non scaricata", "incoerenza geografica", "duplicato"];
function IssuesPane() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/quality/issues", { params: { issue: filter || undefined, limit: 300 } }); setRows(data); }
        catch { /* */ } finally { setLoading(false); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="space-y-3" data-testid="dq-issues-pane">
            <div className="flex items-center gap-2">
                <select data-testid="dq-issues-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 border border-gray-300 px-2 text-sm">
                    <option value="">Tutti i problemi</option>
                    {ISSUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-xs text-gray-500">{rows.length} aste con problemi</span>
            </div>
            <div className="border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Regione</th><th className="px-3 py-2">Provincia</th>
                        <th className="px-3 py-2">Comune</th><th className="px-3 py-2">Problema rilevato</th><th className="px-3 py-2">DQ</th><th className="px-3 py-2">Rilevato</th><th className="px-3 py-2"></th>
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={8} className="px-3 py-4 text-gray-400">Caricamento…</td></tr> :
                            rows.map((r) => (
                                <tr key={r.id} data-testid={`dq-issue-row-${r.id}`} className="border-b border-gray-50 align-top">
                                    <td className="px-3 py-2">{r.source}</td>
                                    <td className="px-3 py-2">{r.region || "—"}</td>
                                    <td className="px-3 py-2">{r.province || "—"}</td>
                                    <td className="px-3 py-2">{r.municipality || <span className="text-rose-600">mancante</span>}</td>
                                    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{r.issues.map((i) => <span key={i} className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5">{i}</span>)}</div></td>
                                    <td className="px-3 py-2"><BandBadge band={r.dq_band} /></td>
                                    <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.detected_at)}</td>
                                    <td className="px-3 py-2">{r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-sky-600"><ArrowSquareOut size={14} /></a>}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------------------------------------------------- Geo Validation
function GeoPane() {
    const [rows, setRows] = useState([]);
    const [onlyBad, setOnlyBad] = useState(false);
    useEffect(() => { api.get("/admin/quality/geo", { params: { status: onlyBad ? "da_verificare" : undefined, limit: 500 } }).then(({ data }) => setRows(data)).catch(() => {}); }, [onlyBad]);
    return (
        <div className="space-y-3" data-testid="dq-geo-pane">
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="dq-geo-onlybad" checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />
                Mostra solo "Da verificare"
            </label>
            <div className="border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="px-3 py-2">Geo confidence</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2">Regione</th>
                        <th className="px-3 py-2">Provincia</th><th className="px-3 py-2">Comune</th><th className="px-3 py-2">Fonte</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`dq-geo-row-${r.id}`} className="border-b border-gray-50">
                                <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${r.geo_confidence === "high" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : r.geo_confidence === "medium" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-rose-50 text-rose-700 border-rose-300"}`}>{r.geo_confidence}</span></td>
                                <td className="px-3 py-2 font-medium">{r.status === "Da verificare" ? <span className="text-rose-600">{r.status}</span> : r.status}</td>
                                <td className="px-3 py-2">{r.region || "—"}</td>
                                <td className="px-3 py-2">{r.province || "—"}</td>
                                <td className="px-3 py-2">{r.municipality || "—"}</td>
                                <td className="px-3 py-2">{r.source}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --------------------------------------------------------------- Duplicates
function DuplicatesPane() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/quality/duplicates", { params: { limit: 100 } }); setGroups(data); }
        catch { /* */ } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const merge = async (keep_id, drop_id) => {
        try { await api.post("/admin/quality/duplicates/merge", { keep_id, drop_id }); toast.success("Record uniti"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const ignore = async (group_key) => {
        try { await api.post("/admin/quality/duplicates/ignore", { group_key }); toast.success("Gruppo ignorato"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (loading) return <p className="text-sm text-gray-400">Caricamento…</p>;
    if (groups.length === 0) return <p className="text-sm text-gray-500" data-testid="dq-duplicates-empty">Nessun possibile duplicato rilevato. 🎉</p>;
    return (
        <div className="space-y-4" data-testid="dq-duplicates-pane">
            {groups.map((g) => (
                <div key={g.group_key} data-testid={`dq-dup-group-${g.group_key}`} className="border border-amber-200 bg-amber-500/[0.07] p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold">{g.count} possibili duplicati</span>
                        <button data-testid={`dq-dup-ignore-${g.group_key}`} onClick={() => ignore(g.group_key)} className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-300 bg-white hover:border-black">
                            <EyeSlash size={13} /> Ignora
                        </button>
                    </div>
                    <div className="space-y-2">
                        {g.records.map((r) => (
                            <div key={r.id} className="flex items-center justify-between bg-white border border-gray-200 px-3 py-2 text-sm">
                                <div>
                                    <span className="font-semibold">{r.municipality || "—"} · {r.province}</span>
                                    <span className="text-gray-500"> · {r.source} · {eur(r.base_price)} · {r.documents} doc · DQ {r.dq_score}</span>
                                    <div className="text-xs text-gray-400">{r.address}</div>
                                </div>
                                <div className="flex gap-1">
                                    {g.records.filter((o) => o.id !== r.id).map((o) => (
                                        <button key={o.id} data-testid={`dq-dup-merge-${r.id}`} onClick={() => merge(r.id, o.id)} title={`Tieni questo, unisci ${o.source}`} className="h-8 px-2.5 inline-flex items-center gap-1 text-xs font-semibold border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50">
                                            <GitMerge size={13} /> Tieni & unisci
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ------------------------------------------------------------ Sources Monitor
function SourcesPane() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/quality/sources").then(({ data }) => setRows(data)).catch(() => {}); }, []);
    return (
        <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="dq-sources-pane">
            <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Aste importate</th><th className="px-3 py-2">Quality score</th>
                    <th className="px-3 py-2">Duplicati</th><th className="px-3 py-2">Ultima scansione</th><th className="px-3 py-2">Errori (24h)</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.source} data-testid={`dq-source-row-${r.source}`} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-medium">{r.source}</td>
                            <td className="px-3 py-2 tabular-nums">{r.imported}</td>
                            <td className="px-3 py-2"><ScoreBar score={r.quality_score} /> <span className="ml-1 font-semibold tabular-nums">{r.quality_score}</span> <BandBadge band={r.quality_band} /></td>
                            <td className="px-3 py-2 tabular-nums">{r.duplicates}</td>
                            <td className="px-3 py-2 text-xs">{fmtDate(r.last_scan_at)}</td>
                            <td className={`px-3 py-2 tabular-nums ${r.scan_errors_24h > 0 ? "text-rose-600 font-bold" : ""}`}>{r.scan_errors_24h}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// -------------------------------------------------------------- Top 100
function TopPane() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/quality/top-opportunities", { params: { limit: 100 } }).then(({ data }) => setRows(data)).catch(() => {}); }, []);
    return (
        <div className="space-y-2" data-testid="dq-top-pane">
            <p className="text-xs text-gray-500">Ordinate per Investment Score → Data Quality → completezza documenti. L'utile netto deriva dall'analisi quando presente, altrimenti è una stima di mercato.</p>
            <div className="border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="px-3 py-2">#</th><th className="px-3 py-2">Investment</th><th className="px-3 py-2">Data Quality</th><th className="px-3 py-2">Doc</th>
                        <th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Regione</th><th className="px-3 py-2">Provincia</th><th className="px-3 py-2">Comune</th>
                        <th className="px-3 py-2">Prezzo base</th><th className="px-3 py-2">Utile netto</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.id} data-testid={`dq-top-row-${r.id}`} className="border-b border-gray-50">
                                <td className="px-3 py-2 tabular-nums text-gray-400">{i + 1}</td>
                                <td className="px-3 py-2 font-bold tabular-nums">{r.investment_score ?? "—"}</td>
                                <td className="px-3 py-2"><span className="tabular-nums font-semibold">{r.dq_score}</span> <BandBadge band={r.dq_band} /></td>
                                <td className="px-3 py-2 tabular-nums">{r.doc_count}</td>
                                <td className="px-3 py-2">{r.source}</td>
                                <td className="px-3 py-2">{r.region || "—"}</td>
                                <td className="px-3 py-2">{r.province || "—"}</td>
                                <td className="px-3 py-2">{r.municipality || "—"}</td>
                                <td className="px-3 py-2 tabular-nums">{eur(r.base_price)}</td>
                                <td className="px-3 py-2 tabular-nums">
                                    {r.net_profit != null ? (
                                        <span className={r.net_profit >= 0 ? "text-emerald-700 font-semibold" : "text-rose-600 font-semibold"}>
                                            {eur(r.net_profit)} {r.net_profit_source === "stima_mercato" && <span className="text-[10px] text-gray-400">(stima)</span>}
                                        </span>
                                    ) : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --------------------------------------------------- Acquisizione Massiva
function BulkAcquisitionCard({ onRefresh }) {
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/quality/acquisition/bulk/status"); setSt(data); }
        catch { /* */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!st?.running) return;
        const t = setInterval(() => { load(); onRefresh?.(); }, 5000);
        return () => clearInterval(t);
    }, [st?.running, load, onRefresh]);

    const start = async () => {
        setBusy(true);
        try { await api.post("/admin/quality/acquisition/bulk/start"); toast.success("Acquisizione massiva avviata in background"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const stop = async () => {
        setBusy(true);
        try { await api.post("/admin/quality/acquisition/bulk/stop"); toast.success("Stop richiesto: il job si fermerà dopo l'asta corrente"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const pct = st?.total ? Math.min(100, Math.round(((st.processed || 0) / st.total) * 100)) : 0;
    return (
        <div className="border border-gray-200 bg-white p-4" data-testid="dq-bulk-acq-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold">Acquisizione Massiva documenti</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Scarica in background perizie e documenti per tutto l'inventario, così ogni analisi parte con la perizia già pronta.
                    </p>
                </div>
                {st?.running ? (
                    <button onClick={stop} disabled={busy} data-testid="dq-bulk-acq-stop"
                            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                        Ferma
                    </button>
                ) : (
                    <button onClick={start} disabled={busy || st?.remaining === 0} data-testid="dq-bulk-acq-start"
                            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                        {st?.remaining === 0 ? "Tutto acquisito" : "Avvia acquisizione"}
                    </button>
                )}
            </div>
            {st && (
                <div className="mt-3 space-y-2" data-testid="dq-bulk-acq-status">
                    {st.running && (
                        <div>
                            <div className="h-1.5 bg-gray-100 overflow-hidden">
                                <div className="h-full bg-emerald-600 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">
                                In corso · {st.processed || 0}/{st.total || 0} aste ({pct}%)
                                {st.last_municipality ? ` · ultima: ${st.last_municipality}` : ""}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600 tabular-nums">
                        <span>Da acquisire: <b>{st.remaining ?? "—"}</b></span>
                        <span>Perizie pronte (deep ready): <b className="text-emerald-700">{st.deep_ready_total ?? "—"}</b></span>
                        <span>Inventario: <b>{st.inventory_total ?? "—"}</b></span>
                        {st.finished_at && !st.running && (
                            <span>Ultima esecuzione: {st.processed || 0} aste · {st.downloaded || 0} doc scaricati · {st.failed || 0} falliti{st.stopped ? " · fermata manualmente" : ""}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// --------------------------------------------------- Document Acquisition Monitor
function AcquisitionPane() {
    const [rows, setRows] = useState([]);
    const [kpi, setKpi] = useState(null);
    const load = useCallback(() => {
        api.get("/admin/quality/acquisition/monitor").then(({ data }) => setRows(data)).catch(() => {});
        api.get("/admin/quality/acquisition/kpis").then(({ data }) => setKpi(data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);
    const Kpi = ({ label, value, suffix = "%" }) => (
        <div className="border border-gray-200 bg-white p-4">
            <div className="text-2xl font-bold tabular-nums">{value == null ? "—" : value + suffix}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
        </div>
    );
    const DimTable = ({ title, dim, testid }) => {
        const entries = Object.entries(dim || {}).filter(([k]) => k !== "—")
            .sort((a, b) => (b[1].auctions || 0) - (a[1].auctions || 0)).slice(0, 12);
        return (
            <div data-testid={testid}>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{title}</h3>
                <div className="border border-gray-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                            <th className="px-3 py-2">Nome</th><th className="px-3 py-2">Success rate</th><th className="px-3 py-2">Coverage</th><th className="px-3 py-2">Aste</th>
                        </tr></thead>
                        <tbody>
                            {entries.map(([k, v]) => (
                                <tr key={k} className="border-b border-gray-50">
                                    <td className="px-3 py-2 font-medium">{k}</td>
                                    <td className="px-3 py-2 tabular-nums">{v.success_rate == null ? "—" : v.success_rate + "%"}</td>
                                    <td className="px-3 py-2 tabular-nums">{v.coverage_pct == null ? "—" : v.coverage_pct + "%"}</td>
                                    <td className="px-3 py-2 tabular-nums">{v.auctions}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };
    return (
        <div className="space-y-6" data-testid="dq-acquisition-pane">
            <BulkAcquisitionCard onRefresh={load} />
            {kpi && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="dq-acq-kpi">
                    <Kpi label="Document Acquisition Success Rate" value={kpi.success_rate} />
                    <Kpi label="Document Coverage % (perizia)" value={kpi.coverage_pct} />
                    <Kpi label="Aste totali" value={kpi.total_auctions} suffix="" />
                </div>
            )}
            <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="dq-acq-monitor-table">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Aste</th><th className="px-3 py-2">Doc trovati</th>
                        <th className="px-3 py-2">Scaricati</th><th className="px-3 py-2">Falliti</th><th className="px-3 py-2">Tasso successo</th>
                        <th className="px-3 py-2">Ultima scansione</th><th className="px-3 py-2">Ultimo errore</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.source} data-testid={`dq-acq-row-${r.source}`} className="border-b border-gray-50">
                                <td className="px-3 py-2 font-medium">{r.source}</td>
                                <td className="px-3 py-2 tabular-nums">{r.auctions_found}</td>
                                <td className="px-3 py-2 tabular-nums">{r.documents_found}</td>
                                <td className="px-3 py-2 tabular-nums text-emerald-700">{r.documents_downloaded}</td>
                                <td className={`px-3 py-2 tabular-nums ${r.downloads_failed > 0 ? "text-rose-600 font-bold" : ""}`}>{r.downloads_failed}</td>
                                <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${ACQ_CLS[r.classification]}`}>{r.success_rate == null ? "n/a" : r.success_rate + "%"}</span></td>
                                <td className="px-3 py-2 text-xs">{fmtDate(r.last_scan_at)}</td>
                                <td className="px-3 py-2 text-xs"><ReasonBadge reason={r.last_error?.reason} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {kpi && (
                <div className="grid md:grid-cols-2 gap-5">
                    <DimTable title="KPI per fonte" dim={kpi.by_source} testid="dq-acq-by-source" />
                    <DimTable title="KPI per regione" dim={kpi.by_region} testid="dq-acq-by-region" />
                </div>
            )}
            {kpi && <DimTable title="KPI per provincia" dim={kpi.by_province} testid="dq-acq-by-province" />}
        </div>
    );
}

// ----------------------------------------------------------- Download Failure Center
function FailuresPane() {
    const [rows, setRows] = useState([]);
    const [onlyUnverified, setOnlyUnverified] = useState(true);
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/quality/acquisition/failures",
                { params: { verified: onlyUnverified ? false : undefined, limit: 300 } });
            setRows(data);
        } catch { /* */ }
    }, [onlyUnverified]);
    useEffect(() => { load(); }, [load]);

    const retry = async (id) => {
        try {
            const { data } = await api.post("/admin/quality/acquisition/retry", { acquisition_id: id });
            if (data.ok) toast.success(`Scaricato (${data.text_len} caratteri)`);
            else toast.error(`Ancora non scaricabile: ${reasonInfo(data.reason).label}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const verify = async (id) => {
        try { await api.post("/admin/quality/acquisition/verify", { acquisition_id: id }); toast.success("Segnato come verificato"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-3" data-testid="dq-failures-pane">
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="dq-failures-unverified" checked={onlyUnverified} onChange={(e) => setOnlyUnverified(e.target.checked)} />
                Mostra solo non verificati
            </label>
            {rows.length === 0 ? <p className="text-sm text-gray-500" data-testid="dq-failures-empty">Nessun download fallito. 🎉</p> : (
                <div className="border border-gray-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                            <th className="px-3 py-2">Asta</th><th className="px-3 py-2">Fonte</th><th className="px-3 py-2">Tipo doc</th>
                            <th className="px-3 py-2">Errore</th><th className="px-3 py-2">Data</th><th className="px-3 py-2">Azioni</th>
                        </tr></thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`dq-failure-row-${r.id}`} className="border-b border-gray-50">
                                    <td className="px-3 py-2">{r.municipality || "—"} · {r.province || "—"}</td>
                                    <td className="px-3 py-2">{r.source}</td>
                                    <td className="px-3 py-2"><span className="text-[10px] uppercase text-gray-500">{r.doc_kind}</span></td>
                                    <td className="px-3 py-2 text-xs"><ReasonBadge reason={r.reason} testid={`dq-failure-reason-${r.id}`} />{r.verified && <span className="ml-2 text-emerald-600">✓ verificato</span>}</td>
                                    <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.attempted_at)}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            <button data-testid={`dq-failure-retry-${r.id}`} onClick={() => retry(r.id)} title="Riprova download" className="h-8 px-2 inline-flex items-center gap-1 text-xs font-semibold border border-sky-300 text-sky-700 bg-white hover:bg-sky-50"><ArrowClockwise size={13} /> Riprova</button>
                                            {r.doc_url && <a href={r.doc_url} target="_blank" rel="noreferrer" title="Apri sorgente" className="h-8 px-2 inline-flex items-center gap-1 text-xs font-semibold border border-gray-300 bg-white hover:border-black"><ArrowSquareOut size={13} /> Sorgente</a>}
                                            {!r.verified && <button data-testid={`dq-failure-verify-${r.id}`} onClick={() => verify(r.id)} title="Segna come verificato" className="h-8 px-2 inline-flex items-center gap-1 text-xs font-semibold border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50"><CheckCircle size={13} /> Verificato</button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function DataQuality() {
    const { user } = useAuth();
    const [tab, setTab] = useState("dashboard");
    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    return (
        <Layout>
            <div className="space-y-6" data-testid="admin-data-quality">
                <div>
                    <h1 className="font-display-black text-3xl flex items-center gap-2">
                        <Database size={26} weight="fill" /> Data Quality
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Controlla la qualità del database nazionale delle aste e individua errori,
                        duplicati, fonti problematiche e dati incompleti prima che arrivino agli utenti.
                    </p>
                </div>
                <nav className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button key={t.key} data-testid={`dq-tab-${t.key}`} role="tab" onClick={() => setTab(t.key)}
                                className={`inline-flex items-center gap-1.5 px-3 h-10 text-sm font-semibold border-b-2 transition-colors ${active ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"}`}>
                                <Icon size={15} weight={active ? "fill" : "regular"} /> {t.label}
                            </button>
                        );
                    })}
                </nav>
                <div>
                    {tab === "dashboard" && <DashboardPane />}
                    {tab === "score" && <ScorePane />}
                    {tab === "issues" && <IssuesPane />}
                    {tab === "geo" && <GeoPane />}
                    {tab === "duplicates" && <DuplicatesPane />}
                    {tab === "acquisition" && <AcquisitionPane />}
                    {tab === "failures" && <FailuresPane />}
                    {tab === "sources" && <SourcesPane />}
                    {tab === "top" && <TopPane />}
                </div>
            </div>
        </Layout>
    );
}
